<template>
  <div>
    <!-- ── HEADER ── -->
    <div class="setting-card mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="pr-3">
          <div class="d-flex align-items-center flex-wrap">
            <h2 class="text-left mb-0 mr-2">{{ formName }}</h2>
            <span v-if="record.status" class="form-status" :class="`form-status-${statusTone}`">
              {{ statusLabel }}
            </span>
          </div>
          <p v-if="!isReadOnly" class="f-12 text-grey mb-0 mt-1 text-left">{{ fillingAs }}</p>
          <!-- <p v-else-if="lockedBy" class="f-12 text-grey mb-0 mt-1 text-left">
            {{ $t("formsFill.lockedBy", { name: lockedBy.name }) }}
          </p> -->
          <div v-else class="d-flex align-items-center flex-wrap mt-2">
            <span class="f-12 text-grey">
              {{ $t("formsFill.submittedBy", { name: filledByName, date: formatDateTime(record.submitted_at) }) }}
            </span>
          </div>
        </div>
        <!-- Handing the form to somebody else is its own action, so it reads as
             a button rather than hiding in a row menu. It only applies while the
             form is still open: a submitted one is a record from then on. -->
        <b-button v-if="!isSubmitted" variant="outline-primary"
          class="mx-0 h-40px px-3 d-flex align-items-center f-14 text-nowrap" @click="openReassignModal">
          {{ $t("formsFill.reassign") }}
        </b-button>
        <!-- Submit at the foot of the form already saves what is filled in, so
             the draft action is parked rather than sitting beside it.
        <b-dropdown v-if="!isReadOnly" id="dropdown-divider" text="text" class="load-dropdown-2 mt-0">
          <template slot="button-content">
            <b-icon class="dots" icon="three-dots"></b-icon>
          </template>
<b-dropdown-item-button @click="saveDraft">
  <p class="text_secondary cursor-pointer mb-0 f-14">{{ $t("formsFill.saveDraft") }}</p>
</b-dropdown-item-button>
</b-dropdown>
-->
      </div>
      <div v-if="isReadOnly" class="meta-row">
        <div class="meta-cell">
          <span class="meta-label">{{ $t("formsFill.assignedTo") }}</span>
          <span class="meta-value">{{ (record.assigned_to && record.assigned_to.name) || "—" }}</span>
        </div>
        <div class="meta-cell">
          <span class="meta-label">{{ $t("formsFill.requestedBy") }}</span>
          <span class="meta-value">{{ (record.assigned_by && record.assigned_by.name) || "—" }}</span>
        </div>
        <div class="meta-cell">
          <span class="meta-label">{{ $t("formsFill.approver") }}</span>
          <span class="meta-value">{{ (record.reviewed_by && record.reviewed_by.name) || "—" }}</span>
        </div>
      </div>
    </div>

    <!-- ── SUBMITTED DATA (read-only) ── -->
    <template v-if="isReadOnly">
      <div class="setting-card mb-4">
        <h4 class="mb-3 pl-0 f-16">{{ $t("formsFill.submittedData") }}</h4>
        <template v-for="entry in submittedEntries">
          <h5 v-if="entry.section" :key="entry.key" class="submitted-section">{{ entry.section }}</h5>
          <div v-else :key="entry.key" class="answer-row" :class="entry.table ? '-m-16px' : ''">
            <span v-if="entry.label" class="meta-label">{{ entry.label }}</span>
            <!-- What was filled in, in the grid it was filled in — the builder's
               own table markup, minus the row actions. -->
            <div v-if="entry.table" class="table-preview-wrap">
              <div class="table-scroll">
                <table class="table-preview">
                  <thead>
                    <tr>
                      <th v-for="column in orderedColumns(entry.table)" :key="`${entry.key}-h-${column.id}`">
                        {{ column.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, lineIndex) in tableRows[entry.table.id] || []"
                      :key="`${entry.key}-r-${lineIndex}`">
                      <td v-for="column in orderedColumns(entry.table)"
                        :key="`${entry.key}-c-${lineIndex}-${column.id}`">
                        <span v-if="isPdf(cellUrl(line[column.id]))" class="cell-thumb cell-file cursor-pointer"
                          @click="openPhoto(cellUrl(line[column.id]))">
                          <b-img :src="require('@/assets/pdf.png')" width="16"></b-img>
                        </span>
                        <b-img v-else-if="isMediaCell(entry.table, column.id, cellUrl(line[column.id]))" :src="cellUrl(line[column.id])"
                          class="cell-thumb cursor-pointer" :class="{ 'cell-thumb--fit': column.type === 'signature' }"
                          @click="openPhoto(cellUrl(line[column.id]))"></b-img>
                        <span v-else>{{ readableAnswer(column, line[column.id]) || "—" }}</span>
                      </td>
                    </tr>
                    <tr v-if="!(tableRows[entry.table.id] || []).length">
                      <td :colspan="orderedColumns(entry.table).length" class="f-12 text-grey text-center">
                        {{ $t("formsFill.noRows") }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else-if="entry.images" class="media-grid mt-1">
              <template v-for="(url, imageIndex) in entry.images">
                <span v-if="isPdf(url)" :key="`${entry.key}-i-${imageIndex}`" class="media-file cursor-pointer"
                  @click="openPhoto(url)">
                  <b-img :src="require('@/assets/pdf.png')" width="48"></b-img>
                </span>
                <b-img v-else :key="`${entry.key}-i-${imageIndex}`" :src="url" class="media-thumb cursor-pointer"
                  :class="{ 'media-thumb--fit': entry.signature }" @click="openPhoto(url)"></b-img>
              </template>
            </div>
            <span v-else class="meta-value">{{ entry.value || "—" }}</span>
          </div>
        </template>
        <p v-if="!submittedEntries.length" class="f-12 text-grey mb-0">{{ $t("formsFill.noAnswers") }}</p>
        <!-- The decision belongs with the answers it is about: the three of
             them on the left, Close on the right. -->
        <div class="form-actions justify-content-start">
          <b-button v-if="canReview" variant="success" class="review-btn mr-3" @click="openReviewModal('approved')">
            {{ $t("formsFill.approve") }}
          </b-button>
          <b-button v-if="canReview" class="review-btn review-btn--changes mr-3"
            @click="openReviewModal('changes_requested')">
            {{ $t("formsFill.requestChanges") }}
          </b-button>
          <b-button v-if="canReview" variant="danger" class="review-btn" @click="openReviewModal('denied')">
            {{ $t("formsFill.deny") }}
          </b-button>
          <b-button variant="secondary"
            class="mx-0 ml-auto h-40px w-100px d-flex justify-content-center align-items-center f-14"
            @click="$emit('close')">
            {{ $t("formsFill.close") }}
          </b-button>
        </div>
      </div>

    </template>

    <!-- ── SECTIONS ── -->
    <!-- Sections and tables are one ordered list, so each renders where the
         API put it rather than sections first and tables after. -->
    <div v-if="!isReadOnly">
      <template v-for="(row, rowIndex) in answerRows">
        <div v-if="!isTable(row)" :key="row.id" class="setting-card mb-4">
          <h4 class="mb-2 pl-0">{{ row.title || $t("formsEditor.sectionChip") }}</h4>
          <div v-for="block in visibleBlocks(row)" :key="block.id" class="fill-field">
            <!-- Content blocks ask nothing; they just say something. -->
            <!-- Instructions are formatted text, so they render as markup. Text
               that came back without any keeps its own line breaks instead. -->
            <div v-if="block.kind === 'content' || block.type === 'instructions'" class="fill-instructions text-left"
              :class="{ 'keep-breaks': !hasMarkup(block.body || block.label) }" v-html="block.body || block.label">
            </div>
            <template v-else>
              <!-- An acknowledgement reads its own label beside the box, so it
                 doesn't get one above as well. -->
              <label v-if="block.type !== 'acknowledgement'" class="fill-label">
                {{ block.label }}
                <span v-if="choiceNote(block)" class="fill-note">{{ choiceNote(block) }}</span>
                <span v-if="block.required" class="fill-required">*</span>
              </label>

              <b-form-input v-if="['text', 'person', 'barcode_scan'].includes(block.type)" :value="answers[block.id]"
                :placeholder="block.placeholder || ''" class="unit" @input="setAnswer(block.id, $event)"></b-form-input>

              <b-form-textarea v-else-if="block.type === 'rich_text'" :value="answers[block.id]" rows="4" class="f-12"
                :placeholder="block.placeholder || $t('formsFill.longTextPlaceholder')"
                @input="setAnswer(block.id, $event)"></b-form-textarea>

              <b-form-input v-else-if="block.type === 'number'" type="number" :value="answers[block.id]"
                :min="block.min" :max="block.max" :placeholder="block.placeholder || ''" class="unit"
                @input="setAnswer(block.id, toNumber($event))"></b-form-input>

              <b-input-group v-else-if="block.type === 'phone'" prepend="+1">
                <b-form-input v-mask="phoneMask(answers[block.id])" :value="answers[block.id]"
                  :placeholder="block.placeholder || ''" class="unit"
                  @input="setAnswer(block.id, $event)"></b-form-input>
              </b-input-group>

              <date-picker v-else-if="block.type === 'date'" :value="dateValue(block.id)" :formatter="momentFormat"
                class="date-picker-custom f-12 w-100" :placeholder="$t('formsFill.selectDate')"
                @change="setDate(block.id, $event)"></date-picker>

              <!-- One answer out of the list. -->
              <div v-else-if="block.type === 'select'" class="fill-choices">
                <b-form-radio v-for="(option, optionIndex) in block.options || []" :key="`${block.id}-${optionIndex}`"
                  :name="`answer-${block.id}`" :value="option" :checked="answers[block.id]" class="text-left"
                  @change="setAnswer(block.id, option)">
                  <span class="f-14">{{ option }}</span>
                </b-form-radio>
              </div>

              <!-- Multiple choice takes as many answers as apply. -->
              <b-form-checkbox-group v-else-if="block.type === 'radio'" stacked class="fill-choices text-left pl-0"
                :checked="asArray(answers[block.id])" :options="block.options || []"
                @input="setAnswer(block.id, $event)"></b-form-checkbox-group>

              <b-form-checkbox-group v-else-if="block.type === 'multiselect'" stacked
                class="fill-choices text-left pl-0" :checked="answers[block.id] || []" :options="block.options || []"
                @input="setAnswer(block.id, $event)"></b-form-checkbox-group>

              <b-form-checkbox v-else-if="block.type === 'acknowledgement'" :checked="answers[block.id] === true"
                class="text-left pl-0" @change="setAnswer(block.id, $event)">
                <span class="f-14">
                  {{ block.label }}
                  <span v-if="block.required" class="fill-required">*</span>
                </span>
              </b-form-checkbox>

              <!-- Driver / Vehicle / Trailer pick a record, then may collect a few
                 of its details as plain text. -->
              <div v-else-if="isCombo(block.type)">
                <v-select append-to-body class="bg_white w-100 f-12" :value="answers[block.id]"
                  :options="comboOptions(block.type)" label="label" @open="searchCombo(block.type, '')"
                  @search="(text) => searchCombo(block.type, text)" @input="setCombo(block, $event)">
                  <template #no-options>
                    {{ block.type === "driver" ? $t("formsFill.searchDrivers") : $t("formsFill.searchUnits") }}
                  </template>
                </v-select>
                <div v-if="(block.sub_fields || []).length" class="sub-fields mt-2">
                  <b-row>
                    <b-col cols="12" sm="6" v-for="sub in block.sub_fields" :key="`${block.id}-${sub.key}`"
                      class="mb-2">
                      <label class="fill-label">
                        {{ sub.label || $t(`formsEditor.sub_${sub.key}`) }}
                        <span v-if="sub.required" class="fill-required">*</span>
                      </label>
                      <!-- Nothing to describe until a record is picked. -->
                      <v-select v-if="isStateKey(sub.key)" append-to-body class="bg_white w-100 f-12"
                        :value="answers[`${block.id}__${sub.key}`]" :options="stateOptions"
                        :disabled="!answers[block.id]"
                        @input="setAnswer(`${block.id}__${sub.key}`, $event || '')"></v-select>
                      <b-form-input v-else :value="answers[`${block.id}__${sub.key}`]" class="unit"
                        :disabled="!answers[block.id]"
                        @input="setAnswer(`${block.id}__${sub.key}`, $event)"></b-form-input>
                    </b-col>
                  </b-row>
                </div>
              </div>

              <!-- A media question takes one file or a set of them, whichever the
                 template asked for. -->
              <div v-else-if="block.type === 'photo'">
                <!-- Every uploaded file, each removable on its own. -->
                <div class="media-grid">
                  <div v-for="(url, mediaIndex) in mediaList(block)" :key="`${block.id}-m-${mediaIndex}`"
                    class="media-item">
                    <span v-if="isPdf(url)" class="media-file cursor-pointer" @click="openPhoto(url)">
                      <b-img :src="require('@/assets/pdf.png')" width="48"></b-img>
                    </span>
                    <b-img v-else :src="url" class="media-thumb cursor-pointer" @click="openPhoto(url)"></b-img>
                    <span class="media-remove" :title="$t('formsFill.removeFile')"
                      @click="removeMedia(block, mediaIndex)">
                      <!-- The bin the image galleries use, so a thumbnail is
                         cleared the same way everywhere. -->
                      <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="30" height="30" rx="8" fill="#F5F5F5" />
                        <path
                          d="M15.2873 7C15.9325 6.99991 16.5544 7.241 17.0309 7.67594C17.5074 8.11088 17.8041 8.70825 17.8628 9.35075H21.8694C22.0481 9.3508 22.2201 9.41868 22.3506 9.54066C22.4812 9.66264 22.5606 9.82964 22.5728 10.0079C22.5849 10.1862 22.529 10.3624 22.4163 10.501C22.3035 10.6396 22.1423 10.7303 21.9653 10.7546L21.8694 10.7612H21.3212L20.1299 21.0857C20.0768 21.544 19.8571 21.9668 19.5126 22.2737C19.1681 22.5805 18.7228 22.7501 18.2615 22.75H12.3131C11.8518 22.7501 11.4065 22.5805 11.062 22.2737C10.7175 21.9668 10.4978 21.544 10.4448 21.0857L9.25248 10.7612H8.70522C8.53481 10.7612 8.37016 10.6995 8.24172 10.5875C8.11329 10.4754 8.02976 10.3207 8.00658 10.1519L8 10.056C8.00001 9.88555 8.06172 9.7209 8.17374 9.59247C8.28575 9.46403 8.44048 9.38051 8.60931 9.35733L8.70522 9.35075H12.7118C12.7705 8.70825 13.0672 8.11088 13.5437 7.67594C14.0202 7.241 14.6421 6.99991 15.2873 7ZM13.8769 12.8769C13.6465 12.8769 13.4537 13.0226 13.4142 13.2144L13.4067 13.2887V19.048L13.4142 19.1214C13.4537 19.3132 13.6465 19.459 13.8769 19.459C14.1072 19.459 14.3 19.3132 14.3395 19.1214L14.347 19.0471V13.2897L14.3395 13.2144C14.3 13.0236 14.1072 12.8769 13.8769 12.8769ZM16.6978 12.8769C16.4674 12.8769 16.2746 13.0226 16.2351 13.2144L16.2276 13.2887V19.048L16.2351 19.1214C16.2746 19.3132 16.4674 19.459 16.6978 19.459C16.9281 19.459 17.1209 19.3132 17.1604 19.1214L17.1679 19.0471V13.2897L17.1604 13.2144C17.1209 13.0236 16.9281 12.8778 16.6978 12.8778V12.8769ZM15.2873 8.41045C14.7184 8.41045 14.2436 8.81478 14.1354 9.35075H16.4392C16.3301 8.81478 15.8562 8.41045 15.2873 8.41045Z"
                          fill="#FF0000" />
                      </svg>
                    </span>
                  </div>
                  <!-- Files go in through the tile at the end of the row. A
                     single-file question keeps it only until it is answered;
                     the bin clears the answer and the tile comes back. -->
                  <label v-if="isMultiMedia(block) || !mediaList(block).length" class="media-add mb-0"
                    :title="uploadLabel(block)">
                    <input type="file" accept="image/*,.pdf" :multiple="isMultiMedia(block)" class="d-none"
                      @change="uploadPhoto(block, $event)" />
                    <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                  </label>
                </div>
              </div>

              <SignaturePad v-else-if="block.type === 'signature'" :value="signatures[block.id] || ''"
                @input="setSignature(block.id, $event)" />

              <b-form-input v-else :value="answers[block.id]" class="unit" @input="setAnswer(block.id, $event)">
              </b-form-input>
              <p v-if="block.help_text" class="fill-help mb-0">{{ block.help_text }}</p>
            </template>
          </div>
          <!-- The actions close the last card rather than adding one of their own. -->
          <div v-if="isLastRow(rowIndex)" class="form-actions">
            <b-button variant="secondary"
              class="mx-0 h-40px w-100px mr-2 d-flex justify-content-center align-items-center f-14"
              @click="$emit('close')">
              {{ $t("formsFill.close") }}
            </b-button>
            <b-button variant="primary"
              class="export equipment d-flex justify-content-center align-items-center w-100px h-40px"
              @click="submitForm">
              <p class="mb-0 text-white p-0">{{ $t("formsFill.submit") }}</p>
            </b-button>
          </div>
        </div>
        <!-- A table asks the same questions once per row. -->
        <div v-else :key="row.id" class="setting-card mb-4">
          <h4 class="mb-3">{{ row.title || $t("formsEditor.tableChip") }}</h4>
          <!-- The builder's own table markup, so the grid a template previews is
             the grid the driver fills in. -->
          <div class="table-preview-wrap">
            <div class="table-scroll">
              <table class="table-preview">
                <thead>
                  <tr>
                    <th v-for="column in orderedColumns(row)" :key="`${row.id}-h-${column.id}`">
                      {{ column.label }}
                      <span v-if="column.required" class="fill-required">*</span>
                    </th>
                    <th class="action-col"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, lineIndex) in tableRows[row.id] || []" :key="`${row.id}-r-${lineIndex}`">
                    <td v-for="column in orderedColumns(row)" :key="`${row.id}-c-${lineIndex}-${column.id}`">
                      <b-form-select v-if="column.type === 'select'" class="f-12" :value="line[column.id]"
                        :options="cellOptions(column)"
                        @change="setCell(row, lineIndex, column.id, $event)"></b-form-select>
                      <v-select v-else-if="column.type === 'radio'" append-to-body class="bg_white f-12"
                        :placeholder="$t('formsFill.selectOption')" :value="line[column.id]"
                        :options="column.options || []" @input="setCell(row, lineIndex, column.id, $event)"></v-select>
                      <b-form-checkbox-group v-else-if="column.type === 'multiselect'"
                        class="cell-choices text-left pl-0" :checked="line[column.id] || []"
                        :options="column.options || []"
                        @input="setCell(row, lineIndex, column.id, $event)"></b-form-checkbox-group>
                      <date-picker v-else-if="column.type === 'date'" :value="cellDate(line[column.id])"
                        :formatter="momentFormat" class="date-picker-custom f-12"
                        :placeholder="$t('formsFill.selectDate')"
                        @change="setCellDate(row, lineIndex, column.id, $event)"></date-picker>
                      <b-input-group v-else-if="column.type === 'phone'" prepend="+1">
                        <b-form-input v-mask="phoneMask(line[column.id])" class="unit f-12" :value="line[column.id]"
                          @input="setCell(row, lineIndex, column.id, $event)"></b-form-input>
                      </b-input-group>
                      <b-form-input v-else-if="column.type === 'number'" type="number" class="unit f-12"
                        :value="line[column.id]"
                        @input="setCell(row, lineIndex, column.id, toNumber($event))"></b-form-input>
                      <v-select v-else-if="isCombo(column.type)" append-to-body class="bg_white f-12"
                        :value="line[column.id]" :options="comboOptions(column.type)" label="label"
                        @open="searchCombo(column.type, '')" @search="(text) => searchCombo(column.type, text)"
                        @input="setCellCombo(row, lineIndex, column, $event)">
                        <template #no-options>
                          {{ column.type === "driver" ? $t("formsFill.searchDrivers") : $t("formsFill.searchUnits") }}
                        </template>
                      </v-select>
                      <!-- A signature is drawn, so the cell opens a pad rather than
                         a file picker. -->
                      <div v-else-if="column.type === 'signature'" class="cell-upload">
                        <b-img v-if="line[column.id]" :src="cellUrl(line[column.id])"
                          class="cell-thumb cell-thumb--fit cursor-pointer" @click="openPhoto(cellUrl(line[column.id]))"></b-img>
                        <b-button variant="link" class="p-0 f-12 upload-btn"
                          @click="openSignatureModal(row, lineIndex, column)">
                          {{ line[column.id] ? $t("formsFill.reSign") : $t("formsFill.sign") }}
                        </b-button>
                        <span v-if="line[column.id]" class="cursor-pointer text-danger ml-1"
                          :title="$t('formsFill.removeFile')" @click="setCell(row, lineIndex, column.id, '')">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5"
                              stroke-linecap="round" />
                          </svg>
                        </span>
                      </div>
                      <!-- A file input doesn't fit a table cell, so it's a button
                         with the uploaded file beside it. -->
                      <div v-else-if="column.type === 'photo'" class="cell-upload">
                        <span v-if="isPdf(cellUrl(line[column.id]))" class="cell-thumb cell-file cursor-pointer"
                          @click="openPhoto(cellUrl(line[column.id]))">
                          <b-img :src="require('@/assets/pdf.png')" width="16"></b-img>
                        </span>
                        <b-img v-else-if="line[column.id]" :src="cellUrl(line[column.id])" class="cell-thumb cursor-pointer"
                          @click="openPhoto(cellUrl(line[column.id]))"></b-img>
                        <label class="upload-btn mb-0">
                          <input type="file" accept="image/*,.pdf" class="d-none"
                            @change="uploadCell(row, lineIndex, column, $event)" />
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M7 9.5V1.5M7 1.5L4 4.5M7 1.5L10 4.5" stroke="currentColor" stroke-width="1.5"
                              stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M1.5 9v2.5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9" stroke="currentColor"
                              stroke-width="1.5" stroke-linecap="round" />
                          </svg>
                          <span class="ml-1">
                            {{ line[column.id] ? $t("formsFill.replace") : $t("formsFill.upload") }}
                          </span>
                        </label>
                        <span v-if="line[column.id]" class="cursor-pointer text-danger ml-1"
                          :title="$t('formsFill.removeFile')" @click="setCell(row, lineIndex, column.id, '')">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5"
                              stroke-linecap="round" />
                          </svg>
                        </span>
                      </div>
                      <b-form-input v-else class="unit f-12" :value="line[column.id]"
                        @input="setCell(row, lineIndex, column.id, $event)"></b-form-input>
                    </td>
                    <td class="action-col">
                      <span class="cursor-pointer" :title="$t('formsFill.removeRow')"
                        @click="removeRow(row, lineIndex)">
                        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                          <path
                            d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                            stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          <path
                            d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                            stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                          <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                            stroke-linejoin="round" />
                        </svg>
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!(tableRows[row.id] || []).length">
                    <td :colspan="orderedColumns(row).length + 1" class="f-12 text-grey text-center">
                      {{ $t("formsFill.noRows") }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <b-button variant="outline-primary" class="ml-10px mt-3 h-40px px-3 d-flex align-items-center f-14"
            @click="addRow(row)">
            <svg class="mr-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            {{ $t("formsFill.addRow") }}
          </b-button>
          <!-- The actions close the last card rather than adding one of their own. -->
          <div v-if="isLastRow(rowIndex)" class="form-actions">
            <b-button variant="secondary"
              class="mx-0 h-40px w-100px mr-2 d-flex justify-content-center align-items-center f-14"
              @click="$emit('close')">
              {{ $t("formsFill.close") }}
            </b-button>
            <b-button variant="primary"
              class="export equipment d-flex justify-content-center align-items-center w-100px h-40px"
              @click="submitForm">
              <p class="mb-0 text-white p-0">{{ $t("formsFill.submit") }}</p>
            </b-button>
          </div>
        </div>
      </template>

    </div>

    <!-- Whoever reviewed it said why, so the note sits above the answers whether
         the form is being read or refilled. -->
    <div v-if="record.review_note" class="review-card mb-3">
      <div class="d-flex align-items-center flex-wrap">
        <span class="review-title mr-2">{{ $t("formsFill.reviewerNote") }}</span>
        <span class="form-status" :class="`form-status-${statusTone}`">{{ statusLabel }}</span>
        <b-button variant="link" class="p-0 f-12 ml-auto d-flex align-items-center review-edit"
          @click="openReviewModal(reviewActionFromStatus, record.review_note)">
          <svg class="mr-1" width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.52719 16.7477L14.5274 7.74753L10.2525 3.4726L1.25226 12.4728C1.12836 12.5969 1.04034 12.7522 0.997548 12.9222L0 18L5.07684 17.0025C5.2473 16.9598 5.40322 16.8717 5.52719 16.7477V16.7477ZM17.4329 4.84205C17.796 4.47881 18 3.98622 18 3.4726C18 2.95898 17.796 2.46639 17.4329 2.10315L15.8968 0.567123C15.5336 0.203994 15.041 0 14.5274 0C14.0138 0 13.5212 0.203994 13.1579 0.567123L11.6219 2.10315L15.8968 6.37808L17.4329 4.84205Z"
              fill="currentColor" />
          </svg>
          {{ $t("formsFill.edit") }}
        </b-button>
      </div>
      <p class="review-note mb-0 mt-2">{{ record.review_note }}</p>
      <hr class="my-2" />
      <p class="f-12 text-grey mb-0">
        {{ $t("formsFill.reviewedBy", {
          name: (record.reviewed_by && record.reviewed_by.name) || "—",
          date: formatDateTime(record.reviewed_at),
        }) }}
      </p>
    </div>

    <!-- Photos open in the zoomable viewer the DVIR review uses. -->
    <b-modal id="modal-form-photo" centered :size="isPdf(activePhoto) ? 'xl' : 'md'"
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'" :body-class="'px-3 pt-2'" :header-class="'align-items-center'"
      @shown="onPhotoShown" @hidden="onPhotoHidden">
      <template #modal-header="{ close }">
        <h5 class="modal-title">{{ activePhotoTitle || $t("formsFill.photo") }}</h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <div class="photo-viewer">
        <!-- A PDF has its own reader, so it opens in a frame rather than the
             zoom viewer — the same way the documents preview shows one. -->
        <div v-if="isPdf(activePhoto)" class="pdf-frame overflow-hidden">
          <iframe class="w-100 h-100 border-0" :src="activePhoto"></iframe>
        </div>
        <div v-else class="photo-frame">
          <div id="form-panzoom-element">
            <img :src="activePhoto" class="photo-image" alt="" />
          </div>
          <div class="d-flex align-items-center justify-content-center zoom-section">
            <b-icon @click="zoom(1)" icon="zoom-in" style="fill: white; cursor: pointer"></b-icon>
            <b-icon @click="zoom(-1)" class="ml-3" icon="zoom-out" style="fill: white; cursor: pointer"></b-icon>
            <span class="text-white f-12 ml-4" v-if="allPhotos.length">
              {{ activeIndex + 1 }} / {{ allPhotos.length }}
            </span>
          </div>
        </div>
        <p v-if="isPdf(activePhoto) && allPhotos.length > 1" class="f-12 text-grey text-center mb-0 mt-2">
          {{ activeIndex + 1 }} / {{ allPhotos.length }}
        </p>
        <!-- Every file on the form is in the deck, so these step through the lot
             rather than just the question that was clicked. -->
        <template v-if="allPhotos.length > 1">
          <span class="photo-nav photo-nav--prev" @click="stepPhoto(-1)">
            <b-icon icon="chevron-left"></b-icon>
          </span>
          <span class="photo-nav photo-nav--next" @click="stepPhoto(1)">
            <b-icon icon="chevron-right"></b-icon>
          </span>
        </template>
      </div>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0" variant="secondary" @click="$bvModal.hide('modal-form-photo')">
            {{ $t("formsFill.close") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-cell-signature" centered no-close-on-backdrop no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'" :body-class="'px-3 pt-2'" :header-class="'align-items-center'">
      <template #modal-header="{ close }">
        <h5 class="modal-title">{{ signatureColumn.label || $t("formsFill.sign") }}</h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <SignaturePad v-model="signatureDraft" />
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary"
            @click="$bvModal.hide('modal-cell-signature')">
            {{ $t("formsFill.cancel") }}
          </b-button>
          <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" :disabled="!signatureDraft"
            @click="saveCellSignature">
            {{ $t("formsFill.save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-review-form" centered no-close-on-backdrop no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'" :body-class="'px-3 pt-2'" :header-class="'align-items-center'">
      <template #modal-header="{ close }">
        <h5 class="modal-title">{{ reviewTitle }}</h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <b-form-group :label="$t('formsFill.note')" class="text-left mb-0">
        <b-form-textarea v-model="reviewNote" rows="4" :placeholder="$t('formsFill.notePlaceholder')"></b-form-textarea>
        <p v-if="reviewError" class="field-error">{{ reviewError }}</p>
      </b-form-group>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary"
            @click="$bvModal.hide('modal-review-form')">
            {{ $t("formsFill.cancel") }}
          </b-button>
          <b-button class="mx-0 mb-0 h-40px px-3 m-0 min-w-100" :variant="reviewVariant" @click="saveReview">
            {{ reviewTitle }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal id="modal-reassign-form" centered no-close-on-backdrop no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'" :body-class="'px-3 pt-2'" :header-class="'align-items-center'">
      <template #modal-header="{ close }">
        <h5 class="modal-title">{{ $t("formsFill.reassign") }}</h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <b-form-group :label="$t('formsFill.reassignTo')" class="text-left mb-3">
        <v-select class="bg_white w-100 f-12" v-model="reassign.member_id" :options="memberOptions" item-value="id"
          item-text="label" :reduce="(option) => option.id" @search="searchMembers">
          <span slot="no-options">{{ $t("formsFill.searchDrivers") }}</span>
        </v-select>
        <p v-if="reassignError" class="field-error">{{ reassignError }}</p>
      </b-form-group>
      <b-form-group :label="$t('formsFill.dueDate')" class="text-left mb-0">
        <date-picker v-model="reassign.due_date" :formatter="momentFormat" class="date-picker-custom f-12 w-100"
          :placeholder="$t('formsFill.selectDate')"></date-picker>
      </b-form-group>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary"
            @click="$bvModal.hide('modal-reassign-form')">
            {{ $t("formsFill.cancel") }}
          </b-button>
          <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" @click="saveReassign">
            {{ $t("formsFill.save") }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import * as moment from "moment-timezone";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import SignaturePad from "@/components/Maintenance/SignaturePad.vue";
import { formStatusKey, formStatusTone } from "@/services/formStatus";
import { checkPhoneFormat, pickerDate } from "@/services/helper";
import Panzoom from "@panzoom/panzoom";

// While the person still has work to do, the form is editable; anything else is
// a record — the same split the API makes.
const OPEN_STATUSES = ["not_started", "in_progress"];

const COMBO_TYPES = ["driver", "vehicle", "trailer"];

// A form that has been approved, sent back or refused has had its decision
// made; what is left is the note saying so.
const REVIEWED_STATUSES = ["approved", "denied", "changes_requested"];

export default {
  name: "FormFill",
  components: { vSelect, DatePicker, SignaturePad },
  props: {
    // { id } — the row created when the form was opened.
    submission: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      record: {},
      answers: {},
      // A table's answer is its list of rows, held per table id while editing.
      tableRows: {},
      // Uploaded files per question: the API keeps one URL in `value` and the
      // whole set in the block's `media`, so the list is tracked separately.
      blockMedia: {},
      // Drawn signatures are data URLs; they become media on submit.
      signatures: {},
      driverOptions: [],
      // One bucket per type: a shared list leaves a trailer question showing
      // whatever a vehicle question searched for last.
      unitOptions: { vehicle: [], trailer: [] },
      memberOptions: [],
      reassign: { member_id: null, due_date: null },
      reassignError: "",
      // The cell being signed, and the drawing before it is uploaded.
      signatureCell: null,
      signatureColumn: {},
      signatureDraft: "",
      // The file the viewer is on, and its zoom.
      activeIndex: 0,
      panzoom: null,
      reviewAction: "approved",
      reviewNote: "",
      reviewError: "",
      momentFormat: {
        stringify: (date) => (date ? moment(date).format("ll") : ""),
        parse: (value) => (value ? moment(value).toDate() : null),
      },
    };
  },
  computed: {
    ...mapGetters(["allTeamMembers", "allDrivers", "user", "allStates", "currentTeamMember", "rolePermissions"]),
    // States are picked from the list the app already loads, not typed.
    stateOptions() {
      return (this.allStates || []).map((state) => state.name);
    },
    allowUpdateForms() {
      return this.rolePermissions && this.rolePermissions['Forms'] && this.rolePermissions['Forms'][1].allow
    },
    // Render from the row's own copy of the questions, never the live template.
    // `answer_rows` is the shape the API answers with; the older keys are kept as
    // a fallback so a cached response still renders.
    answerRows() {
      const rows =
        this.record.answer_rows || this.record.definition_snapshot || this.record.definition || [];
      // `order` is authoritative; don't rely on array position.
      return [...rows].sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    // Columns carry their own order too.
    orderedColumns() {
      return (table) => [...(table.columns || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    formName() {
      const template = this.record.template;
      return (template && template.name) || this.record.template_name || this.$t("formsFill.untitled");
    },
    // Once handed in, the form is a record: read it, don't edit it.
    // The id the API files a submission under; assigned_to carries the same one.
    currentMemberId() {
      const member = (this.user && this.user.team_member) || {};
      return member.id || (this.user && this.user.id) || null;
    },
    isSubmitted() {
      // Nothing is decided until the record arrives: an empty status is not a
      // status, and reading it as "submitted" flashes the review buttons up
      // for as long as the fetch takes.
      if (!this.record.id) return false;
      return !OPEN_STATUSES.includes(formStatusKey(this.record.status));
    },
    // A form belongs to whoever it was assigned to: a second person opening it
    // reads it, they don't fill it in on top of the first one's answers.
    lockedBy() {
      if (this.isSubmitted) return null;
      const owner = this.record.assigned_to;
      if (!owner || !owner.id || !this.currentMemberId) return null;
      return `${owner.id}` === `${this.currentMemberId}` ? null : owner;
    },
    isReviewed() {
      return REVIEWED_STATUSES.includes(formStatusKey(this.record.status));
    },
    // Approve, send back and deny are for a form that is in and still waiting on
    // an answer; once one has been given, changing it goes through Edit on the
    // reviewer's note rather than pressing the same button twice.
    canReview() {
      return this.isSubmitted && !this.isReviewed;
    },
    // Once handed in, the form is a record: read it, don't edit it. The same
    // goes for an open form that belongs to somebody else, and for anyone
    // without permission to update forms at all.
    isReadOnly() {
      return this.isSubmitted || !!this.lockedBy || !this.allowUpdateForms;
    },
    statusLabel() {
      const key = `formsPage.status_${formStatusKey(this.record.status)}`;
      const label = this.$t(key);
      return label === key ? this.record.status : label;
    },
    statusTone() {
      return formStatusTone(this.record.status);
    },
    filledByName() {
      const person = this.record.filled_by || this.record.assigned_to;
      return (person && person.name) || "—";
    },
    // Editing the note keeps the decision that was made.
    reviewActionFromStatus() {
      const key = formStatusKey(this.record.status);
      return ["approved", "denied", "changes_requested"].includes(key) ? key : "approved";
    },
    // Denying is the one decision that reads as a refusal, so its button says so.
    reviewVariant() {
      return this.reviewAction === "denied" ? "danger" : "primary";
    },
    reviewTitle() {
      if (this.reviewAction === "denied") return this.$t("formsFill.deny");
      if (this.reviewAction === "changes_requested") return this.$t("formsFill.requestChanges");
      return this.$t("formsFill.approve");
    },
    // Every answered question, flattened for reading.
    submittedEntries() {
      const entries = [];
      this.answerRows.forEach((row) => {
        if (this.isTable(row)) {
          const rows = this.tableRows[row.id] || [];
          if (rows.length) {
            entries.push({
              key: `${row.id}-title`,
              section: row.title || this.$t("formsEditor.tableChip"),
            });
            entries.push({ key: row.id, table: row });
          }
          return;
        }
        // The section's name goes in front of its answers, but only once it has
        // some — an all-instructions section says nothing here.
        const before = entries.length;
        (row.items || row.blocks || []).forEach((item) => this.collectEntry(item, entries));
        if (entries.length > before) {
          entries.splice(before, 0, {
            key: `${row.id}-title`,
            section: row.title || this.$t("formsEditor.sectionChip"),
          });
        }
      });
      return entries;
    },
    // Every file answered on the form, so the viewer can page through them all.
    allPhotos() {
      const photos = [];
      const add = (url, name) => {
        if (typeof url === "string" && /^https?:/.test(url)) photos.push({ url, name });
      };
      const walk = (item) => {
        if (!item) return;
        if (["photo", "signature"].includes(item.type)) {
          this.mediaList(item).forEach((url) => add(url, item.label));
        }
        const logic = item.conditional_logic;
        if (logic) (logic.fields || []).forEach(walk);
      };
      this.answerRows.forEach((row) => {
        if (!this.isTable(row)) {
          (row.items || row.blocks || []).forEach(walk);
          return;
        }
        const columns = this.orderedColumns(row).filter((column) =>
          ["photo", "signature"].includes(column.type)
        );
        (this.tableRows[row.id] || []).forEach((line) => {
          columns.forEach((column) => add(this.cellUrl(line[column.id]), column.label));
        });
      });
      return photos;
    },
    activePhoto() {
      const photo = this.allPhotos[this.activeIndex];
      return photo ? photo.url : "";
    },
    activePhotoTitle() {
      const photo = this.allPhotos[this.activeIndex];
      return photo ? photo.name : "";
    },
    fillingAs() {
      const name =
        (this.record.assigned_to && this.record.assigned_to.name) ||
        (this.user && this.user.name) ||
        "";
      return this.$t("formsFill.fillingAs", { name });
    },
  },
  methods: {
    ...mapActions([
      "getFormSubmission",
      "updateFormSubmission",
      "submitFormSubmission",
      "reassignFormSubmission",
      "reviewFormSubmission",
      "uploadFormMedia",
      "getFilteredEquipments",
      "getAllTeamMembers",
      "getAllDrivers",
      "getTeamMember",
      "getAllStates",
      "setNotification",
      "setLoader",
    ]),
    // One label/value pair per answered question, sub-fields included.
    collectEntry(item, entries) {
      if (!item || item.kind === "content" || item.type === "instructions") return;
      const value = this.answers[item.id];
      // Files come from the block's media, never from `value`: the URL stored
      // there was signed when the form was submitted and has since expired.
      const uploaded = ["photo", "signature"].includes(item.type) ? this.mediaList(item) : [];
      const isImage = !!uploaded.length;
      // Files read as a row of thumbnails whether there is one or several.
      const images = uploaded.length ? uploaded : null;
      entries.push({
        key: item.id,
        label: item.label,
        signature: item.type === "signature",
        value: isImage ? uploaded[0] : this.readableAnswer(item, value),
        image: isImage,
        images,
      });
      (item.sub_fields || []).forEach((sub) => {
        const subValue = this.answers[`${item.id}__${sub.key}`];
        if (subValue) {
          entries.push({
            key: `${item.id}__${sub.key}`,
            label: `${item.label} — ${sub.label || this.$t(`formsEditor.sub_${sub.key}`)}`,
            value: subValue,
          });
        }
      });
      const logic = item.conditional_logic;
      if (logic) (logic.fields || []).forEach((field) => this.collectEntry(field, entries));
    },
    readableAnswer(item, value) {
      if (value === null || value === undefined || value === "") return "";
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "boolean") return value ? this.$t("documentsPage.yes") : this.$t("documentsPage.no");
      if (typeof value === "object") return value.label || value.name || "";
      if (item.type === "date") return moment(value).format("ll");
      return `${value}`;
    },
    formatDateTime(value) {
      return value ? moment(value).format("lll") : "—";
    },
    openReviewModal(action, note) {
      this.reviewAction = action;
      this.reviewNote = note || "";
      this.reviewError = "";
      this.$bvModal.show("modal-review-form");
    },
    // The API refuses a denial or a send-back without a reason, so ask here.
    async saveReview() {
      const note = this.reviewNote.trim();
      if (!note && this.reviewAction !== "approved") {
        this.reviewError = this.$t("formsFill.noteRequired");
        return;
      }
      try {
        this.setLoader(true);
        await this.reviewFormSubmission({
          id: this.submission.id,
          payload: { action: this.reviewAction, note },
        });
        this.$bvModal.hide("modal-review-form");
        await this.fetchSubmission();
        await this.setNotification({
          msg: this.$t("formsFill.reviewSaved"),
          type: "success",
          color: "green",
        });
      } catch (error) {
        await this.setNotification({
          msg: this.apiError(error),
          type: "error",
          color: "red",
        });
      } finally {
        this.setLoader(false);
      }
    },
    hasMarkup(value) {
      return /<[a-z][\s\S]*>/i.test(`${value || ""}`);
    },
    // The S3 links are signed, so the extension has to be read before the query.
    isPdf(url) {
      return `${url || ""}`.split("?")[0].toLowerCase().endsWith(".pdf");
    },
    openPhoto(url) {
      const index = this.allPhotos.findIndex((photo) => photo.url === url);
      this.activeIndex = index === -1 ? 0 : index;
      this.$bvModal.show("modal-form-photo");
    },
    // Wraps around at both ends; the zoom is dropped so each photo opens at 1x.
    stepPhoto(step) {
      if (!this.allPhotos.length) return;
      const count = this.allPhotos.length;
      this.activeIndex = (this.activeIndex + step + count) % count;
      this.destroyPanzoom();
    },
    onPhotoShown() {
      window.addEventListener("keydown", this.onPhotoKey);
    },
    onPhotoHidden() {
      window.removeEventListener("keydown", this.onPhotoKey);
      this.destroyPanzoom();
      this.activeIndex = 0;
    },
    onPhotoKey(event) {
      if (event.key === "ArrowLeft") this.stepPhoto(-1);
      if (event.key === "ArrowRight") this.stepPhoto(1);
    },
    // Built on first zoom and dropped when the modal closes, so the next photo
    // starts unscaled.
    zoom(level) {
      if (!this.panzoom) {
        this.panzoom = Panzoom(document.getElementById("form-panzoom-element"), { maxScale: 5 });
      }
      level === -1 ? this.panzoom.zoomOut() : this.panzoom.zoomIn();
    },
    destroyPanzoom() {
      if (this.panzoom) this.panzoom.destroy();
      this.panzoom = null;
    },
    isLastRow(index) {
      return index === this.answerRows.length - 1;
    },
    isTable(row) {
      return !!row && (row.type === "table" || !!row.columns);
    },
    isStateKey(key) {
      return key === "plate_state" || key === "license_state";
    },
    // A dropdown needs an empty first entry so it can be left unanswered.
    selectOptions(block) {
      return [{ value: null, text: this.$t("formsFill.selectOption") }].concat(
        (block.options || []).map((option) => ({ value: option, text: option }))
      );
    },
    // An answer saved before the field took several picks comes back as a
    // string, so it is wrapped rather than dropped.
    asArray(value) {
      if (Array.isArray(value)) return value;
      return value === null || value === undefined || value === "" ? [] : [value];
    },
    // "(Select one)" or "(Select all that apply)", so the question says what it
    // expects before anybody starts answering it.
    choiceNote(block) {
      if (block.type === "select") return this.$t("formsFill.selectOne");
      if (["radio", "multiselect"].includes(block.type)) return this.$t("formsFill.selectAll");
      return "";
    },
    isCombo(type) {
      return COMBO_TYPES.includes(type);
    },
    phoneMask(value) {
      return `${value || ""}`.length > 0
        ? [/\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/]
        : false;
    },
    // The first table cell holding something that isn't a phone number.
    badPhoneCell() {
      let found = null;
      this.answerRows.forEach((table) => {
        if (found || !this.isTable(table)) return;
        const columns = this.orderedColumns(table).filter((column) => column.type === "phone");
        if (!columns.length) return;
        (this.tableRows[table.id] || []).forEach((line, index) => {
          if (found) return;
          columns.forEach((column) => {
            const value = `${line[column.id] || ""}`.trim();
            if (!found && value && !checkPhoneFormat(value)) {
              found = { label: column.label || "", row: index + 1 };
            }
          });
        });
      });
      return found;
    },
    // Every question on the form, including the ones a condition reveals.
    allQuestions() {
      const questions = [];
      const walk = (block) => {
        questions.push(block);
        const logic = block.conditional_logic;
        if (logic) (logic.fields || []).forEach(walk);
      };
      this.answerRows.forEach((row) => {
        if (this.isTable(row)) return;
        (row.items || row.blocks || []).forEach(walk);
      });
      return questions;
    },
    async fetchSubmission() {
      try {
        this.setLoader(true);
        const data = await this.getFormSubmission(this.submission.id);
        this.record = data || {};
        this.answers = this.seedAnswers(data);
        this.tableRows = this.seedTables(data);
        this.blockMedia = this.seedMedia(data);
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    // Each question carries its own answer, so the form is seeded from the same
    // tree it renders — `answers` is only the working copy that goes back up.
    seedAnswers(data) {
      const answers = { ...((data && data.answers) || {}) };
      const rows = (data && data.answer_rows) || [];
      rows.forEach((entry) => {
        (entry.items || entry.blocks || []).forEach((item) => this.seedBlock(item, answers));
      });
      return answers;
    },
    seedBlock(item, answers) {
      if (!item || !item.id) return;
      if (item.value !== null && item.value !== undefined) answers[item.id] = item.value;
      (item.sub_fields || []).forEach((sub) => {
        if (sub && sub.value !== null && sub.value !== undefined) {
          answers[`${item.id}__${sub.key}`] = sub.value;
        }
      });
      const logic = item.conditional_logic;
      if (logic) (logic.fields || []).forEach((field) => this.seedBlock(field, answers));
    },
    // Everything already uploaded, filed under the question it belongs to. The
    // submission's own `media` list carries `block_ref`, which also covers the
    // questions a condition reveals.
    seedMedia(data) {
      const media = {};
      // The same file can be uploaded twice, so it is the media id that says
      // whether two entries are the same record — the signed URLs are identical.
      const add = (blockId, entry) => {
        if (!blockId || !entry || !entry.url) return;
        const list = media[blockId] || [];
        // Ids may be numbers or uuids, and the same record can arrive as either
        // from the block and from the submission's own media list.
        const seen = list.some((item) =>
          entry.id !== null && entry.id !== undefined
            ? `${item.id}` === `${entry.id}`
            : item.url === entry.url
        );
        if (!seen) list.push({ id: entry.id, url: entry.url });
        media[blockId] = list;
      };
      const walk = (item) => {
        if (!item || !item.id) return;
        (item.media || []).forEach((entry) => add(item.id, entry));
        const logic = item.conditional_logic;
        if (logic) (logic.fields || []).forEach(walk);
      };
      ((data && data.answer_rows) || []).forEach((entry) => {
        (entry.items || entry.blocks || []).forEach(walk);
      });
      ((data && data.media) || []).forEach((entry) => add(entry.block_ref, entry));
      return media;
    },
    // A table starts with the rows it already has, never fewer than min_rows.
    seedTables(data) {
      const tables = {};
      ((data && data.answer_rows) || []).forEach((entry) => {
        if (!entry || (entry.type !== "table" && !entry.columns)) return;
        // A saved row comes back as a list of cells ([{id, value}, …]); the
        // editor works in { columnId: value }, so it is folded here. A row that
        // is already keyed is kept as it is.
        const rows = (entry.rows || []).map((row) =>
          Array.isArray(row)
            ? row.reduce(
              (cells, cell) => (cell && cell.id ? { ...cells, [cell.id]: cell.value } : cells),
              {}
            )
            : { ...row }
        );
        const minRows = entry.min_rows || 0;
        while (rows.length < minRows) rows.push({});
        tables[entry.id] = rows.length ? rows : [{}];
      });
      return tables;
    },
    // b-table takes its columns from the table's own definition, plus one for
    // the delete action.
    tableFields(table) {
      return this.orderedColumns(table)
        .map((column) => ({
          key: column.id,
          label: `${column.label}${column.required ? " *" : ""}`,
          thClass: "font-table-weight-bold",
        }))
        .concat([{ key: "__action", label: "", thClass: "font-table-weight-bold" }]);
    },
    isMediaCell(table, key, value) {
      const column = this.columnFor(table, key);
      return ["photo", "signature"].includes(column.type) && typeof value === "string" && value;
    },
    columnFor(table, key) {
      return this.orderedColumns(table).find((column) => column.id === key) || {};
    },
    cellOptions(column) {
      return [{ value: null, text: "" }].concat(
        (column.options || []).map((option) => ({ value: option, text: option }))
      );
    },
    cellDate(value) {
      return pickerDate(value) || null;
    },
    setCell(table, rowIndex, columnId, value) {
      const rows = this.tableRows[table.id] || [];
      const row = { ...(rows[rowIndex] || {}), [columnId]: value };
      this.$set(rows, rowIndex, row);
      this.$set(this.tableRows, table.id, rows);
    },
    setCellCombo(table, rowIndex, column, option) {
      this.setCell(table, rowIndex, column.id, option ? { id: option.id, label: option.label } : null);
    },
    setCellDate(table, rowIndex, columnId, value) {
      this.setCell(table, rowIndex, columnId, value ? moment(value).format("YYYY-MM-DD") : "");
    },
    async uploadCell(table, rowIndex, column, event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      try {
        this.setLoader(true);
        const entry = await this.sendMedia(
          file,
          `${table.id}__${rowIndex}__${column.id}`,
          column.type === "signature" ? "signature" : "image"
        );
        if (entry) this.setCell(table, rowIndex, column.id, entry.url);
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    openSignatureModal(table, rowIndex, column) {
      this.signatureCell = { table, rowIndex, column };
      this.signatureColumn = column;
      this.signatureDraft = "";
      this.$bvModal.show("modal-cell-signature");
    },
    // A drawing is a data URL; it goes up as a PNG so the cell holds a URL like
    // every other file answer.
    async saveCellSignature() {
      if (!this.signatureCell || !this.signatureDraft) return;
      const { table, rowIndex, column } = this.signatureCell;
      try {
        this.setLoader(true);
        const file = await this.signatureFile(this.signatureDraft, `${column.id}.png`);
        const entry = await this.sendMedia(file, `${table.id}__${rowIndex}__${column.id}`, "signature");
        if (entry) this.setCell(table, rowIndex, column.id, entry.url);
        this.$bvModal.hide("modal-cell-signature");
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    addRow(table) {
      const rows = [...(this.tableRows[table.id] || []), {}];
      this.$set(this.tableRows, table.id, rows);
    },
    removeRow(table, rowIndex) {
      const rows = [...(this.tableRows[table.id] || [])];
      rows.splice(rowIndex, 1);
      this.$set(this.tableRows, table.id, rows.length ? rows : [{}]);
    },
    // Numbers go up as numbers; an empty box is no answer, not zero.
    toNumber(value) {
      if (value === "" || value === null || value === undefined) return "";
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    },
    // Table rows ride in `answers` under the table's id, like any other answer.
    answersPayload() {
      const payload = { ...this.answers };
      Object.keys(this.tableRows).forEach((tableId) => {
        payload[tableId] = this.tableRows[tableId].filter((row) =>
          Object.keys(row).some((key) => row[key] !== "" && row[key] !== null && row[key] !== undefined)
        );
      });
      return payload;
    },
    // A field inside a condition only appears once its question is answered that way.
    visibleBlocks(section) {
      const items = [...(section.items || section.blocks || [])].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      return items.reduce((blocks, block) => {
        blocks.push(block);
        const logic = block.conditional_logic;
        if (!logic || !(logic.fields || []).length) return blocks;
        const answer = this.answers[block.id];
        const holds = Array.isArray(answer) ? answer.includes(logic.value) : answer === logic.value;
        const matches = logic.condition === "not_equals" ? !holds : holds;
        return matches ? blocks.concat(logic.fields) : blocks;
      }, []);
    },
    setAnswer(key, value) {
      this.$set(this.answers, key, value);
    },
    dateValue(id) {
      const value = this.answers[id];
      return pickerDate(value) || null;
    },
    setDate(id, value) {
      this.setAnswer(id, value ? moment(value).format("YYYY-MM-DD") : "");
    },
    isChecked(id, option) {
      return (this.answers[id] || []).includes(option);
    },
    toggleAnswer(id, option, checked) {
      const current = Array.isArray(this.answers[id]) ? [...this.answers[id]] : [];
      if (checked) current.push(option);
      this.setAnswer(id, checked ? current : current.filter((entry) => entry !== option));
    },
    comboOptions(type) {
      if (type === "driver") return this.driverOptions;
      return this.unitOptions[type] || [];
    },
    async searchCombo(type, searchText) {
      try {
        if (type === "driver") {
          // Drivers, not team members: get-all-users answers with role=no_drivers.
          await this.getAllDrivers(`page=1&page_size=20&name=${searchText || ""}`);
          // The whole record rides along so picking one can fill its details in.
          this.driverOptions = (this.allDrivers || []).map((driver) => {
            const user = driver.user || {};
            return {
              id: driver.id,
              label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || driver.name || `${driver.id}`,
              record: driver,
            };
          });
          return;
        }
        if (!searchText) return;
        // The unit type and the in-service statuses are filters the API applies,
        // so a vehicle question never offers a trailer or a retired unit.
        const records = await this.getFilteredEquipments({
          unit_no: searchText,
          type: type === "trailer" ? "Trailer" : "Vehicle",
          status: "Active,Assigned",
        });
        this.$set(
          this.unitOptions,
          type,
          (records || []).map((item) => ({
            id: item.id,
            label: item.unit_no,
            record: item,
          }))
        );
      } catch (error) {
        console.error("Error searching:", error);
      }
    },
    // The API stores the picked record as {id, label}.
    async setCombo(block, option) {
      this.setAnswer(block.id, option ? { id: option.id, label: option.label } : null);
      if (!option) {
        this.fillSubFields(block, null);
        return;
      }
      let record = option.record;
      // The driver search row has no licence number, so the full record is read
      // for the details and the row's own fields kept on top of it.
      if (block.type === "driver") {
        try {
          await this.getTeamMember({ id: option.id, fetch_user: false });
          if (this.currentTeamMember) record = { ...this.currentTeamMember, ...record };
        } catch (error) {
          console.error("Error loading driver:", error);
        }
      }
      this.fillSubFields(block, record);
    },
    // Picking a unit or a driver answers its sub-fields too — they are the same
    // details the record already holds, so nobody should retype them.
    fillSubFields(block, record) {
      if (!record) return;
      const details =
        block.type === "driver"
          ? {
            license: record.license_number || record.license_no,
            license_state: this.stateName(record.license_state),
            phone: record.phone || (record.user && record.user.phone),
            email: (record.user && record.user.email) || record.email,
          }
          : {
            vin: record.vin_no,
            year: record.year,
            make: record.make,
            model: record.model,
            plate: record.plate_no,
            plate_state: this.stateName(record.state),
          };
      (block.sub_fields || []).forEach((sub) => {
        const value = details[sub.key];
        // A blank on the record leaves the box empty rather than writing "null".
        this.setAnswer(`${block.id}__${sub.key}`, value === null || value === undefined ? "" : `${value}`);
      });
    },
    // A state comes back either as {id, name} or as a bare id — look the id up in
    // the list so the picker shows a state either way.
    stateName(state) {
      if (state && typeof state === "object") return state.name || "";
      if (state === null || state === undefined || state === "") return "";
      const match = (this.allStates || []).find((entry) => `${entry.id}` === `${state}`);
      return match ? match.name : "";
    },
    setSignature(id, value) {
      this.$set(this.signatures, id, value);
    },
    // A media question the template marked as multiple keeps its answer as a
    // list of URLs; a single one stays the plain string it has always been.
    isMultiMedia(block) {
      return block.multiple === true;
    },
    mediaList(block) {
      return this.mediaEntries(block).map((entry) => entry.url);
    },
    // The signature on an S3 link changes per request, so two links to the same
    // file only match once the query is dropped.
    mediaPath(url) {
      return `${url || ""}`.split("?")[0];
    },
    // A link inside an answer was signed when the file went up and lasts an
    // hour; the submission's own media list is signed again on every read, so
    // the file is looked up there by its path before it is shown.
    freshUrl(url) {
      const path = this.mediaPath(url);
      if (!path) return "";
      const match = ((this.record && this.record.media) || []).find(
        (entry) => this.mediaPath(entry.url) === path
      );
      return match ? match.url : url;
    },
    // A table cell holds a single file, but the answer can still arrive as a
    // list; either way what is shown is one live link.
    cellUrl(value) {
      const url = Array.isArray(value) ? value[0] : value;
      return typeof url === "string" ? this.freshUrl(url) : "";
    },
    // A multiple-file question answers with the whole list of URLs, which is
    // also the shape it comes back in; a single one stays the plain string it
    // has always been.
    setMedia(block, entries) {
      const list = entries.filter((entry) => entry && entry.url);
      const urls = list.map((entry) => entry.url);
      this.$set(this.blockMedia, block.id, list);
      if (this.isMultiMedia(block)) {
        this.setAnswer(block.id, urls);
        return;
      }
      this.setAnswer(block.id, urls.length ? urls[0] : "");
    },
    mediaEntries(block) {
      const uploaded = this.blockMedia[block.id] || [];
      const value = this.answers[block.id];
      // Nothing filed under the question, so the answer itself names the files:
      // a list for a multiple-file question, one URL for a single one.
      if (!uploaded.length) {
        return (Array.isArray(value) ? value : [value])
          .filter((url) => typeof url === "string" && url)
          .map((url) => ({ id: null, url: this.freshUrl(url) }));
      }
      if (this.isMultiMedia(block)) return [...uploaded];
      // A single-file question keeps every upload on the server, so replacing
      // the file leaves the one it replaced behind. The answer names the file
      // that counts; failing that, the newest upload wins.
      const path = this.mediaPath(value);
      const named = uploaded.filter((entry) => this.mediaPath(entry.url) === path);
      return (named.length ? named : uploaded).slice(-1);
    },
    removeMedia(block, index) {
      const list = this.mediaEntries(block);
      list.splice(index, 1);
      this.setMedia(block, list);
    },
    // The panel says what tapping it does: add to a set, swap the one file, or
    // attach the first one.
    uploadLabel(block) {
      if (this.isMultiMedia(block)) return this.$t("formsFill.attachFiles");
      return this.mediaList(block).length
        ? this.$t("formsFill.replaceFile")
        : this.$t("formsFill.attachFile");
    },
    async uploadPhoto(block, event) {
      const files = Array.from((event.target && event.target.files) || []);
      if (!files.length) return;
      try {
        this.setLoader(true);
        const uploaded = [];
        for (const file of files) {
          const entry = await this.sendMedia(file, block.id, "image");
          if (entry) uploaded.push(entry);
        }
        // Picking again adds to the set when several are allowed; a single-file
        // question replaces what was there.
        this.setMedia(block, this.isMultiMedia(block) ? this.mediaEntries(block).concat(uploaded) : uploaded);
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        // The same file can be picked again only if the input is cleared.
        if (event.target) event.target.value = "";
        this.setLoader(false);
      }
    },
    async sendMedia(file, blockRef, mediaType) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("block_ref", blockRef);
      formData.append("media_type", mediaType);
      const data = await this.uploadFormMedia({ id: this.submission.id, formData });
      const url = data && (data.url || data.file);
      return url ? { id: data.id, url } : null;
    },
    // A drawn signature is a data URL — it goes up as a file so the answer can
    // hold the URL the API returns, like a photo does.
    // The pad hands back its whole canvas, most of which is empty, so a saved
    // signature reads as a squiggle in a big white box wherever it is shown
    // afterwards. This crops it to what was actually drawn, with a small margin.
    async trimSignature(dataUrl) {
      try {
        const image = await new Promise((resolve, reject) => {
          const loading = new Image();
          loading.onload = () => resolve(loading);
          loading.onerror = reject;
          loading.src = dataUrl;
        });
        const source = document.createElement("canvas");
        source.width = image.width;
        source.height = image.height;
        const context = source.getContext("2d");
        context.drawImage(image, 0, 0);
        const { data } = context.getImageData(0, 0, source.width, source.height);
        let top = source.height;
        let left = source.width;
        let right = -1;
        let bottom = -1;
        for (let y = 0; y < source.height; y += 1) {
          for (let x = 0; x < source.width; x += 1) {
            const at = (y * source.width + x) * 4;
            // Ink is anything neither transparent nor near-white.
            const drawn =
              data[at + 3] > 10 && !(data[at] > 245 && data[at + 1] > 245 && data[at + 2] > 245);
            if (!drawn) continue;
            if (x < left) left = x;
            if (x > right) right = x;
            if (y < top) top = y;
            if (y > bottom) bottom = y;
          }
        }
        if (right < left || bottom < top) return dataUrl;
        const pad = 12;
        left = Math.max(0, left - pad);
        top = Math.max(0, top - pad);
        right = Math.min(source.width - 1, right + pad);
        bottom = Math.min(source.height - 1, bottom + pad);
        const cropped = document.createElement("canvas");
        cropped.width = right - left + 1;
        cropped.height = bottom - top + 1;
        cropped
          .getContext("2d")
          .drawImage(source, left, top, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
        return cropped.toDataURL("image/png");
      } catch (error) {
        // A drawing that cannot be measured still goes up as it was drawn.
        console.error("Error trimming signature:", error);
        return dataUrl;
      }
    },
    async signatureFile(dataUrl, name) {
      const trimmed = await this.trimSignature(dataUrl);
      const blob = await (await fetch(trimmed)).blob();
      return new File([blob], name, { type: "image/png" });
    },
    async uploadSignatures() {
      const entries = Object.keys(this.signatures).filter((id) => this.signatures[id]);
      for (const id of entries) {
        const file = await this.signatureFile(this.signatures[id], `${id}.png`);
        const entry = await this.sendMedia(file, id, "signature");
        if (entry) this.setAnswer(id, entry.url);
      }
    },
    async saveDraft() {
      try {
        this.setLoader(true);
        await this.uploadSignatures();
        await this.updateFormSubmission({
          id: this.submission.id,
          payload: { answers: this.answersPayload() },
        });
        await this.setNotification({
          msg: this.$t("formsFill.draftSaved"),
          type: "success",
          color: "green",
        });
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    async submitForm() {
      // A half-typed number reads as a real answer to the API, so it is caught here.
      const badPhone = this.allQuestions().find(
        (block) =>
          block.type === "phone" &&
          `${this.answers[block.id] || ""}`.trim() &&
          !checkPhoneFormat(`${this.answers[block.id]}`.trim())
      );
      if (badPhone) {
        await this.setNotification({
          msg: this.$t("formsFill.wrongPhone", { label: badPhone.label || "" }),
          type: "error",
          color: "red",
        });
        return;
      }
      const badCell = this.badPhoneCell();
      if (badCell) {
        await this.setNotification({
          msg: this.$t("formsFill.wrongPhoneRow", { label: badCell.label, row: badCell.row }),
          type: "error",
          color: "red",
        });
        return;
      }
      try {
        this.setLoader(true);
        await this.uploadSignatures();
        await this.submitFormSubmission({
          id: this.submission.id,
          payload: { answers: this.answersPayload() },
        });
        await this.setNotification({
          msg: this.$t("formsFill.submitted"),
          type: "success",
          color: "green",
        });
        this.$emit("close");
      } catch (error) {
        // The API answers a failed submit with the questions still empty — that
        // list is ready to read, so it goes out as the error itself.
        const missing = (error && error.missing) || [];
        await this.setNotification({
          msg: missing.length
            ? this.$t("formsFill.missing", { fields: missing.join(", ") })
            : this.apiError(error),
          type: "error",
          color: "red",
        });
      } finally {
        this.setLoader(false);
      }
    },
    openReassignModal() {
      this.reassign = { member_id: null, due_date: null };
      this.reassignError = "";
      this.$bvModal.show("modal-reassign-form");
    },
    async searchMembers(searchText) {
      if (!searchText) return;
      try {
        await this.getAllDrivers(`page=1&page_size=20&name=${searchText}`);
        this.memberOptions = (this.allDrivers || []).map((driver) => {
          const user = driver.user || {};
          return {
            id: driver.id,
            label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || driver.name || `${driver.id}`,
            record: driver,
          };
        });
      } catch (error) {
        console.error("Error searching drivers:", error);
      }
    },
    async saveReassign() {
      if (!this.reassign.member_id) {
        this.reassignError = this.$t("formsFill.reassignRequired");
        return;
      }
      const payload = { member_id: Number(this.reassign.member_id) };
      if (this.reassign.due_date) {
        payload.due_date = moment(this.reassign.due_date).format("YYYY-MM-DD");
      }
      try {
        this.setLoader(true);
        await this.reassignFormSubmission({ id: this.submission.id, payload });
        this.$bvModal.hide("modal-reassign-form");
        await this.setNotification({
          msg: this.$t("formsFill.reassigned"),
          type: "success",
          color: "green",
        });
        this.$emit("close");
      } catch (error) {
        this.reassignError = this.apiError(error);
      } finally {
        this.setLoader(false);
      }
    },
    apiError(error) {
      if (typeof error === "string") return error;
      if (error && error.error) return error.error;
      if (error && error.detail) return error.detail.error || error.detail;
      const firstField = error && Object.values(error)[0];
      if (Array.isArray(firstField)) return firstField[0];
      return firstField || this.$t("formsFill.saveFailed");
    },
  },
  created() {
    this.fetchSubmission();
    if (!(this.allStates || []).length) this.getAllStates();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onPhotoKey);
    this.destroyPanzoom();
  },
};
</script>
<style scoped>
.setting-card {
  padding: 1rem;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}

/* A section's name reads as a heading, not as a label above the fields. */
.setting-card h4 {
  margin-bottom: 1rem;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.fill-field {
  margin-top: 16px;
}

.fill-field:first-of-type {
  margin-top: 0;
}

/* Whatever the builder's rich-text field produced, at reading size. */
.fill-instructions {
  font-size: 13px;
  color: #212529;
}

.fill-instructions>>>p:last-child {
  margin-bottom: 0;
}

/* Plain text keeps the blank lines and indents it was written with. */
.keep-breaks {
  white-space: pre-wrap;
}

/* ── READ-ONLY RECORD ── */
/* The badge from the Forms table, so a status reads the same everywhere. */
.form-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 4px;
  color: #ffffff;
  font-size: 14px;
  /* The same fixed width the Forms list uses. */
  min-width: 100px;
  white-space: nowrap;
}

.form-status-success {
  background-color: #2cce89;
}

.form-status-primary {
  background-color: #2d69f6;
}

.form-status-warning {
  background-color: #ffc800;
}

.form-status-danger {
  background-color: #ff0000;
}

.form-status-secondary {
  background-color: #f4f4f4 !important;
  border: 1px solid #eaeaea !important;
  color: black !important;
}

.record-chip {
  padding: 0 8px;
  border-radius: 4px;
  background: #f8f9fb;
  border: 1px solid #e0e0e0;
  font-size: 12px;
  color: #6c757d;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.meta-cell {
  flex: 1 1 160px;
  min-width: 0;
}

.meta-label {
  display: block;
  font-size: 13px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6c757d;
}

.meta-value {
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #212529;
  word-break: break-word;
  /* Long-text answers keep the line breaks they were typed with. */
  white-space: pre-wrap;
}

.answer-row {
  margin-bottom: 16px;
}

/* The section a group of answers came from. */
.submitted-section {
  margin: 30px 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
}

.submitted-section:first-of-type {
  margin-top: 0;
}

/* The reviewer's decision, set apart from the answers it is about. */
.review-card {
  padding: 1rem;
  text-align: left;
  background: white;
  border: 1px solid #c8d0e4;
  border-radius: 0.25rem;
}

.review-title {
  font-size: 14px;
  font-weight: 500;
  color: #212529;
}

.review-edit {
  color: #2d69f6;
  text-decoration: none;
}

.review-note {
  font-size: 14px;
  color: #212529;
}

.fill-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  color: #212529;
}

/* The "(Select one)" hint beside a question. */
.fill-note {
  margin-left: 4px;
  font-size: 12px;
  color: #6c757d;
}

.fill-required {
  color: #2d69f6;
}

.fill-choices {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Inside a table cell the boxes run across, so a row stays one line tall. */
.cell-choices>>>.custom-control {
  display: inline-flex;
  margin-right: 12px;
}

/* The details a driver/vehicle/trailer answer also collects, grouped. */
.sub-fields {
  padding: 12px;
  background: #f8f9fb;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}

/* A cell's height comes from its tallest control, and the multi-select was
   stretching to fill it — pin it to the same 40px the inputs use. */
.table-preview .v-select>>>.vs__dropdown-toggle {
  min-height: 40px;
  height: auto;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  background: #fafafa;
}

.table-preview .v-select>>>.vs__selected-options {
  padding: 2px 4px;
}

.table-preview .v-select>>>.vs__actions {
  padding-top: 0;
}

/* ── TABLE ── */
/* The builder's preview rules, unchanged (see .table-preview in the editor). */
.table-preview-wrap {
  padding: 16px 12px;
  padding-bottom: 0px;
}

.table-scroll {
  overflow-x: auto;
}

.table-preview {
  width: 100%;
  border-collapse: collapse;
}

.table-preview th,
.table-preview td {
  min-width: 140px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  font-size: 13px;
  color: #212529;
}

.table-preview td {
  vertical-align: middle;
}

/* The delete column carries an icon, not an answer. */
.table-preview th.action-col,
.table-preview td.action-col {
  min-width: 40px;
  width: 40px;
}

/* Inside a cell the boxes run across, so a row stays one line tall. */
.table-preview .cell-choices>>>.custom-control {
  display: inline-flex;
  margin-right: 12px;
}

.fill-help {
  margin-top: 4px;
  font-size: 11px;
  color: #6c757d;
}

/* Actions sitting at the foot of the last card. */
.form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

/* ── REVIEW ACTIONS ── */
.review-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  max-width: 100%;
  height: 40px;
  margin: 0;
  font-size: 14px;
}

/* Sending it back is neither good news nor bad, so it stays neutral. */
.review-btn--changes,
.review-btn--changes:hover {
  background-color: #ffffff;
  border: 1px solid #e2e6f0;
  color: #222736;
}

/* ── UPLOADS ── */
.cell-upload {
  display: flex;
  align-items: center;
  gap: 6px;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  background: #ffffff;
  color: #2d69f6;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}

.upload-btn:hover {
  border-color: #2d69f6;
  background-color: #f8f9fb;
}

/* The uploaded file, small enough to sit inside a cell. */
.cell-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 0.25rem;
  border: 1px solid #e0e0e0;
}

/* ── PHOTO VIEWER ── */
/* Holds the zoomed image; the bar below it stays put while the image pans. */
.photo-frame {
  max-height: 70vh;
  overflow: hidden;
}

.photo-image {
  display: block;
  width: auto;
  max-width: 100%;
  /* Leaves room for the zoom bar inside 70vh, so the modal never scrolls. */
  max-height: 62vh;
  margin: 0 auto;
}

.photo-frame #form-panzoom-element {
  overflow: hidden;
}

/* The arrows sit outside the panning frame so they stay put. */
.photo-viewer {
  position: relative;
}

.photo-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  cursor: pointer;
}

.photo-nav--prev {
  left: 8px;
}

.photo-nav--next {
  right: 8px;
}

.pdf-frame {
  height: 70vh;
}

/* A PDF has no thumbnail, so it gets a tile the size of one. */
.media-file {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 0.25rem;
  border: 1px solid #e0e0e0;
  background: #f8f9fb;
  color: #e5252a;
}

.cell-file {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fb;
  color: #e5252a;
}

/* Several files read as a row of thumbnails rather than a stack of images. */
.media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.media-item {
  position: relative;
}

.media-thumb {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 0.25rem;
  border: 1px solid #e0e0e0;
}

/* A signature is ink on nothing — no tile, no frame, just the drawing at the
   same height as the thumbnails beside it. */
.media-thumb--fit,
.cell-thumb--fit {
  object-fit: contain;
  background: none;
  border: none;
  border-radius: 0;
  width: auto;
}

/* The tile that adds one more file, sized like a thumbnail. */
.media-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border: 1px dashed #2d69f6;
  border-radius: 0.25rem;
  background: rgba(45, 105, 246, 0.08);
  color: #2d69f6;
  cursor: pointer;
}

.media-add:hover {
  background: rgba(45, 105, 246, 0.14);
}

/* The bin sits on the thumbnail itself; the icon carries its own plate. */
.media-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  line-height: 0;
  cursor: pointer;
}
</style>
